/* istanbul ignore file */

import { JsonPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { GetBundlesResult, LiveUpdate } from '@capawesome/capacitor-live-update';
import { ButtonComponent } from '@libs/common/components';
import { ComponentType } from '@angular/cdk/overlay';
import { LiveUpdateService } from '@libs/shared/live-update';
import { SpinnerOverlayService, APP_UPDATE_DIALOG_COMPONENT_TYPE, DialogService } from '@libs/shared/common';

@Component({
  selector: 'ui-live-update-debugger',
  imports: [ButtonComponent, MatInput, FormsModule, MatFormField, MatLabel, JsonPipe],
  templateUrl: './live-update-debugger.component.html',
  styleUrl: './live-update-debugger.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LiveUpdateDebuggerComponent implements OnInit {
  public version: WritableSignal<string> = signal('');
  public error: WritableSignal<string> = signal('');
  public v1: WritableSignal<string> = signal('');
  public v2: WritableSignal<string> = signal('');
  public isNewer: WritableSignal<boolean> = signal(false);
  public availableBundles: WritableSignal<string[]> = signal([]);
  public currentVersion: WritableSignal<string> = signal('');
  private readonly liveUpdateService: LiveUpdateService = inject(LiveUpdateService);
  private readonly spinner: SpinnerOverlayService = inject(SpinnerOverlayService);
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly appUpdateDialogComponentCtr: ComponentType<unknown> = inject(APP_UPDATE_DIALOG_COMPONENT_TYPE);

  public ngOnInit(): void {
    void this.setBundlesAndCurrentVersion();
  }

  public async applyUpdate(version: string): Promise<void> {
    this.spinner.showLoader();
    this.error.set('');

    try {
      await this.liveUpdateService.checkForAndApplyUpdate(version);
      await this.setBundlesAndCurrentVersion();
    } catch (ex) {
      this.error.set(JSON.stringify(ex));
    } finally {
      this.spinner.hideLoader();
    }
  }

  public reloadView(): Promise<void> {
    return this.liveUpdateService.reloadWebViewToUseUpdate();
  }

  public async testUrlRestorationOnLiveUpdateReload(url: string): Promise<void> {
    localStorage.setItem('live-update-previous-url', url);

    await LiveUpdate.reload();
  }

  public async removeAllButCurrentBundle(): Promise<void> {
    const bundles: GetBundlesResult = await LiveUpdate.getBundles();
    const currentVersion: string | null = (await LiveUpdate.getCurrentBundle()).bundleId;

    for (const id of bundles.bundleIds) {
      if (id === currentVersion) {
        continue;
      }

      await LiveUpdate.deleteBundle({ bundleId: id });
    }

    await this.setBundlesAndCurrentVersion();
  }

  public openNativeUpdateDialog(): void {
    this.dialogService.open(this.appUpdateDialogComponentCtr, {
      closeOnNavigation: false,
      disableClose: true,
      hasBackdrop: true,
    });
  }

  public testSemver(): void {
    this.isNewer.set(this.liveUpdateService.isNewerVersion(this.v2(), this.v1()));
  }

  private async setBundlesAndCurrentVersion(): Promise<void> {
    const bundles: GetBundlesResult = await LiveUpdate.getBundles();

    this.availableBundles.set(bundles.bundleIds.sort());

    this.currentVersion.set((await LiveUpdate.getCurrentBundle()).bundleId ?? 'none');
  }
}
